import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, BookOpen, Eye, Palette, X, Check, ArrowRight, Home, Layers, History, Image as ImageIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { API_ENDPOINTS } from '../config/backendConfig';

export default function MentorMagicPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [uploadedArtworks, setUploadedArtworks] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const [showArtworkSelection, setShowArtworkSelection] = useState(false);
  const [previousArtworks, setPreviousArtworks] = useState([]);
  const [loadingArtworks, setLoadingArtworks] = useState(false);
  // Filters for previous artworks
  const [filterArtist, setFilterArtist] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterSection, setFilterSection] = useState('');
  // Artwork Library (Art History) states
  const [showArtworkLibrary, setShowArtworkLibrary] = useState(false);
  const [artHistoryArtworks, setArtHistoryArtworks] = useState([]);
  const [loadingArtHistory, setLoadingArtHistory] = useState(false);
  const [selectedArtHistoryIndex, setSelectedArtHistoryIndex] = useState(0);
  // Mentor Artwork Library states
  const [showMentorArtworkLibrary, setShowMentorArtworkLibrary] = useState(false);
  const [mentorArtworks, setMentorArtworks] = useState([]);
  const [loadingMentorArtworks, setLoadingMentorArtworks] = useState(false);
  const [filteredMentorArtworks, setFilteredMentorArtworks] = useState([]);
  // Filters for Mentor Artwork Library
  const [filterMentorArtist, setFilterMentorArtist] = useState('');
  const [filterMentorClass, setFilterMentorClass] = useState('');
  const [filterMentorSection, setFilterMentorSection] = useState('');

  // Helper function to normalize image URLs (similar to ArtStyleSelectionPage)
  const getFullImageUrl = (imageUrl) => {
    if (!imageUrl) return imageUrl;
    
    // Data/blobs are already resolvable by the browser
    if (imageUrl.startsWith('data:') || imageUrl.startsWith('blob:')) {
      return imageUrl;
    }
    
    // Full URLs (including Firebase Storage URLs)
    if (/^https?:\/\//i.test(imageUrl)) {
      return imageUrl;
    }
    
    // Absolute uploads path
    if (imageUrl.startsWith('/uploads')) {
      return `${API_ENDPOINTS.backendBase}${imageUrl}`;
    }
    
    // Relative uploads path (missing leading slash)
    if (imageUrl.startsWith('uploads')) {
      return `${API_ENDPOINTS.backendBase}/${imageUrl}`;
    }
    
    // Bare filename (e.g., abc.jpg) -> assume uploads
    if (!imageUrl.startsWith('/')) {
      return `${API_ENDPOINTS.backendBase}/uploads/${imageUrl}`;
    }
    
    return imageUrl;
  };

  const features = [
    {
      id: 'appreciation',
      title: 'Art Appreciation',
      icon: BookOpen,
      description: 'Analyze student artworks and provide detailed appreciation with technique insights',
      color: 'from-amber-500 to-orange-500',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200'
    },
    {
      id: 'ways-of-seeing',
      title: 'Ways of Seeing',
      icon: Eye,
      description: 'Transform student artworks into different art styles and movements. Explore 50+ art styles',
      color: 'from-orange-500 to-amber-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200'
    },
    {
      id: 'reimagine',
      title: 'Re-imagine Artwork',
      icon: Palette,
      description: 'Reimagine and enhance student artworks with creative transformations',
      color: 'from-amber-600 to-yellow-500',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200'
    }
  ];

  const handleFeatureSelect = (featureId) => {
    setSelectedFeature(featureId);
    // Show artwork selection modal instead of upload modal
    setShowArtworkSelection(true);
    loadPreviousArtworks();
  };

  // Load previously processed artworks for mentor
  const loadPreviousArtworks = async () => {
    if (!user?.uid) return;
    
    try {
      setLoadingArtworks(true);
      const activitiesQuery = query(
        collection(db, 'userActivities'),
        where('metadata.context', '==', 'mentor'),
        where('metadata.mentorId', '==', user.uid)
      );

      const snapshot = await getDocs(activitiesQuery);
      const activities = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort client-side by createdAt desc
      activities.sort((a, b) => {
        const ad = a.createdAt?.toDate ? a.createdAt.toDate() : a.createdAt;
        const bd = b.createdAt?.toDate ? b.createdAt.toDate() : b.createdAt;
        return new Date(bd) - new Date(ad);
      });

      // Filter out art history and comparison activities - only show mentor-uploaded artworks
      const validFeatures = ['ways-of-seeing', 'reimagine', 'reimagine-artwork', 'ways-of-seeing-journey', 'reimagine-with-feedback'];
      const filteredActivities = activities.filter(activity => {
        const feature = activity.metadata?.feature || activity.type || '';
        const activityType = activity.type || '';
        
        // Exclude art history and comparison activities
        if (feature === 'art-history-appreciation' || 
            feature === 'art-comparison' ||
            activityType === 'art-history-appreciation' ||
            activityType === 'art-comparison') {
          return false;
        }
        
        // Only include "Lets Play with Art" features (mentor-uploaded artworks)
        return validFeatures.includes(feature);
      });

      // Group activities by artwork (originalImageUrl)
      const artworkGroups = {};
      filteredActivities.forEach(activity => {
        const artworkUrl = activity.originalImageUrl;
        console.log('🎨 MentorMagicPage: Processing activity:', {
          id: activity.id,
          originalImageUrl: artworkUrl,
          title: activity.title,
          artistName: activity.metadata?.artistName,
          feature: activity.metadata?.feature
        });
        
        if (!artworkUrl) {
          console.warn('⚠️ MentorMagicPage: Activity missing originalImageUrl:', activity.id);
          return; // Skip activities without artwork URL
        }
        
        // Get student name from various possible locations
        const studentName = activity.metadata?.artistName || 
                           activity.metadata?.artworkMeta?.artist || 
                           activity.metadata?.studentName || 
                           'Unknown Artist';
        
        // Only include artworks with student name, class, and section (mentor-uploaded)
        const className = activity.metadata?.className || '';
        const section = activity.metadata?.section || '';
        
        if (!studentName || studentName === 'Unknown Artist' || !className || !section) {
          console.log('⚠️ MentorMagicPage: Skipping activity without proper metadata:', {
            id: activity.id,
            studentName,
            className,
            section
          });
          return;
        }
        
        if (!artworkGroups[artworkUrl]) {
          artworkGroups[artworkUrl] = {
            artworkUrl,
            title: activity.title || 'Untitled Artwork',
            artist: studentName,
            className: className,
            section: section,
            activities: []
          };
        }
        artworkGroups[artworkUrl].activities.push({
          id: activity.id,
          type: activity.type,
          feature: activity.metadata?.feature || 'unknown',
          createdAt: activity.createdAt,
          title: activity.title
        });
      });

      const artworkList = Object.values(artworkGroups);
      console.log('🎨 MentorMagicPage: Loaded', artworkList.length, 'artworks');
      console.log('🎨 MentorMagicPage: Artwork URLs:', artworkList.map(a => a.artworkUrl));
      setPreviousArtworks(artworkList);
    } catch (error) {
      console.error('Error loading previous artworks:', error);
    } finally {
      setLoadingArtworks(false);
    }
  };

  const [uploadedArtwork, setUploadedArtwork] = useState(null);
  const [artworkName, setArtworkName] = useState('');
  const [artistName, setArtistName] = useState('');
  const [classNameSelection, setClassNameSelection] = useState(() => {
    try { return localStorage.getItem('mentorClass') || ''; } catch { return ''; }
  });
  const [sectionSelection, setSectionSelection] = useState(() => {
    try { return localStorage.getItem('mentorSection') || ''; } catch { return ''; }
  });

  const handleFileUpload = (files) => {
    const newArtworks = Array.from(files).map((file, index) => ({
      id: Date.now() + index,
      file,
      name: file.name,
      preview: URL.createObjectURL(file),
      feature: selectedFeature
    }));
    
    setUploadedArtworks(prev => [...prev, ...newArtworks]);
    // Keep the modal open and show the uploaded artwork
    setUploadedArtwork(newArtworks[0]); // Show the first uploaded artwork
    // Set default names from filename
    const fileName = newArtworks[0].name.replace(/\.[^/.]+$/, ""); // Remove extension
    setArtworkName(fileName);
    setArtistName('');
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files);
    }
  };


  const removeArtwork = (artworkId) => {
    setUploadedArtworks(prev => {
      const updated = prev.filter(artwork => artwork.id !== artworkId);
      return updated;
    });
  };

  const processArtwork = (artwork) => {
    // Store artwork data for the feature
    localStorage.setItem('mentorSelectedArtwork', JSON.stringify({
      file: artwork.preview,
      name: artwork.customName || artwork.name,
      artist: artwork.customArtist || 'Unknown Artist',
      feature: artwork.feature,
      isMentorUpload: true,
      className: classNameSelection || null,
      section: sectionSelection || null
    }));
    
    // Navigate directly to the analysis output page for each feature
    const featureRoutes = {
      'appreciation': '/magic', // Use the individual artist magic page for art appreciation
      'ways-of-seeing': '/art-world/selection', // Navigate to art style selection page
      'reimagine': '/reimagine-studio'
    };
    
    navigate(featureRoutes[artwork.feature], { 
      state: { 
        fromMentorDashboard: true,
        artwork: {
          imageUrl: artwork.preview,
          originalArtwork: artwork.preview,
          title: artwork.customName || artwork.name,
          artist: artwork.customArtist || 'Unknown Artist'
        }
      } 
    });
  };

  const handleProcessArtwork = () => {
    if (uploadedArtwork && artworkName.trim() && artistName.trim()) {
      // Update artwork with custom names
      const updatedArtwork = {
        ...uploadedArtwork,
        customName: artworkName.trim(),
        customArtist: artistName.trim()
      };
      
      processArtwork(updatedArtwork);
      // Close modal after processing
      setShowUploadModal(false);
      setSelectedFeature(null);
      setUploadedArtwork(null);
      setArtworkName('');
      setArtistName('');
    }
  };

  const handleSelectPreviousArtwork = (artwork) => {
    console.log('🎨 MentorMagicPage: handleSelectPreviousArtwork called');
    console.log('🎨 MentorMagicPage: Full artwork object:', JSON.stringify(artwork, null, 2));
    console.log('🎨 MentorMagicPage: artwork.artworkUrl:', artwork?.artworkUrl);
    console.log('🎨 MentorMagicPage: artwork type:', typeof artwork);
    console.log('🎨 MentorMagicPage: artwork keys:', Object.keys(artwork || {}));
    console.log('🎨 MentorMagicPage: selectedFeature:', selectedFeature);
    
    // Validate artwork URL - try multiple possible properties
    const artworkUrl = artwork?.artworkUrl || artwork?.imageUrl || artwork?.originalArtwork || artwork?.url || null;
    
    if (!artworkUrl) {
      console.error('❌ MentorMagicPage: Artwork URL is missing!');
      console.error('❌ MentorMagicPage: Full artwork object:', artwork);
      console.error('❌ MentorMagicPage: artwork.artworkUrl:', artwork?.artworkUrl);
      console.error('❌ MentorMagicPage: artwork.imageUrl:', artwork?.imageUrl);
      console.error('❌ MentorMagicPage: artwork.originalArtwork:', artwork?.originalArtwork);
      console.error('❌ MentorMagicPage: artwork.url:', artwork?.url);
      alert('Error: Artwork URL is missing. Please try selecting another artwork.');
      return;
    }
    
    // Normalize the artwork URL to ensure it's in the correct format
    const normalizedArtworkUrl = getFullImageUrl(artworkUrl);
    console.log('✅ MentorMagicPage: Validated artwork URL:', artworkUrl.substring(0, 100));
    console.log('✅ MentorMagicPage: Normalized artwork URL:', normalizedArtworkUrl.substring(0, 100));
    
    // Store artwork data for the feature
    localStorage.setItem('mentorSelectedArtwork', JSON.stringify({
      file: normalizedArtworkUrl,
      name: artwork.title,
      artist: artwork.artist,
      feature: selectedFeature,
      isMentorUpload: true,
      className: artwork.className || null,
      section: artwork.section || null,
      isPreviousArtwork: true
    }));
    
    // Navigate to the analysis page
    const featureRoutes = {
      'appreciation': '/magic',
      'ways-of-seeing': '/art-world/selection', // Navigate to art style selection page
      'reimagine': '/reimagine-studio'
    };
    
    const artworkState = {
      imageUrl: normalizedArtworkUrl,
      originalArtwork: normalizedArtworkUrl,
      url: normalizedArtworkUrl, // Add url property as well
      artworkUrl: normalizedArtworkUrl, // Add artworkUrl property as well
      title: artwork.title,
      artist: artwork.artist
    };
    
    console.log('🎨 MentorMagicPage: Navigating with artwork state:', JSON.stringify(artworkState, null, 2));
    console.log('🎨 MentorMagicPage: Route:', featureRoutes[selectedFeature]);
    console.log('🎨 MentorMagicPage: Original artwork URL:', artworkUrl);
    console.log('🎨 MentorMagicPage: Normalized artwork URL being passed:', normalizedArtworkUrl);
    console.log('🎨 MentorMagicPage: Full navigation state:', {
      fromMentorDashboard: true,
      artwork: artworkState
    });
    
    navigate(featureRoutes[selectedFeature], { 
      state: { 
        fromMentorDashboard: true,
        artwork: artworkState
      } 
    });
  };

  const handleCloseModal = () => {
    setShowUploadModal(false);
    setSelectedFeature(null);
    setUploadedArtwork(null);
    setArtworkName('');
    setArtistName('');
  };

  const handleCloseArtworkSelection = () => {
    setShowArtworkSelection(false);
    setSelectedFeature(null);
  };

  // Fetch art history artworks for the library
  const fetchArtHistoryArtworks = async () => {
    try {
      setLoadingArtHistory(true);
      console.log('🔄 [MENTOR] Fetching art history artworks from Firebase...');
      
      // Try with orderBy first (for artworks with createdAt)
      let querySnapshot;
      try {
        const q = query(
          collection(db, 'artHistoryArtworks'),
          orderBy('createdAt', 'desc')
        );
        querySnapshot = await getDocs(q);
        console.log('✅ [MENTOR] Query with orderBy succeeded:', querySnapshot.docs.length, 'documents');
      } catch (orderByError) {
        console.warn('⚠️ [MENTOR] orderBy query failed, trying without orderBy:', orderByError);
        // Fallback: fetch without orderBy if createdAt index is missing
        const q = query(collection(db, 'artHistoryArtworks'));
        querySnapshot = await getDocs(q);
        console.log('✅ [MENTOR] Query without orderBy succeeded:', querySnapshot.docs.length, 'documents');
      }
      
      const artworksData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        // Ensure createdAt exists (use updatedAt or current time as fallback)
        if (!data.createdAt) {
          data.createdAt = data.updatedAt || new Date();
        }
        return {
          id: doc.id,
          ...data
        };
      });
      
      // Sort by createdAt if available (client-side fallback)
      artworksData.sort((a, b) => {
        const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt?.getTime ? a.createdAt.getTime() : 0);
        const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt?.getTime ? b.createdAt.getTime() : 0);
        return bTime - aTime; // Descending order
      });
      
      console.log('✅ [MENTOR] Fetched art history artworks for library:', artworksData.length);
      if (artworksData.length > 0) {
        console.log('📋 [MENTOR] Sample artwork:', artworksData[0]);
      } else {
        console.warn('⚠️ [MENTOR] No artworks found in collection!');
      }
      setArtHistoryArtworks(artworksData);
    } catch (error) {
      console.error('❌ [MENTOR] Error fetching art history artworks:', error);
      console.error('Error details:', error.message, error.code);
      setArtHistoryArtworks([]);
    } finally {
      setLoadingArtHistory(false);
    }
  };

  // Handle art history artwork selection
  const handleSelectArtHistoryArtwork = (artwork) => {
    console.log('🎨 MentorMagicPage: Selected art history artwork:', artwork);
    
    // Normalize the artwork URL
    const normalizedArtworkUrl = getFullImageUrl(artwork.imageUrl);
    
    // Store artwork data
    localStorage.setItem('mentorSelectedArtwork', JSON.stringify({
      file: normalizedArtworkUrl,
      name: artwork.title || 'Art History Artwork',
      artist: artwork.artist || 'Unknown Artist',
      feature: 'ways-of-seeing',
      isMentorUpload: true,
      isArtHistory: true
    }));
    
    // Navigate to art style selection page
    const artworkState = {
      imageUrl: normalizedArtworkUrl,
      originalArtwork: normalizedArtworkUrl,
      url: normalizedArtworkUrl,
      artworkUrl: normalizedArtworkUrl,
      title: artwork.title || 'Art History Artwork',
      artist: artwork.artist || 'Unknown Artist'
    };
    
    console.log('🎨 MentorMagicPage: Navigating to art style selection with:', artworkState);
    
    // Close the library modal
    setShowArtworkLibrary(false);
    setShowArtworkSelection(false);
    
    // Navigate to art style transfer page
    navigate('/art-world/selection', { 
      state: { 
        fromMentorDashboard: true,
        artwork: artworkState
      } 
    });
  };

  // Fetch mentor-uploaded artworks for the Mentor Artwork Library
  const fetchMentorArtworks = async () => {
    if (!user?.uid) return;
    
    try {
      setLoadingMentorArtworks(true);
      const activitiesQuery = query(
        collection(db, 'userActivities'),
        where('metadata.context', '==', 'mentor'),
        where('metadata.mentorId', '==', user.uid)
      );

      const snapshot = await getDocs(activitiesQuery);
      const activities = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filter out art history and comparison activities - only show mentor-uploaded artworks
      const validFeatures = ['ways-of-seeing', 'reimagine', 'reimagine-artwork', 'ways-of-seeing-journey', 'reimagine-with-feedback'];
      const filteredActivities = activities.filter(activity => {
        const feature = activity.metadata?.feature || activity.type || '';
        const activityType = activity.type || '';
        
        // Exclude art history and comparison activities
        if (feature === 'art-history-appreciation' || 
            feature === 'art-comparison' ||
            activityType === 'art-history-appreciation' ||
            activityType === 'art-comparison') {
          return false;
        }
        
        // Only include "Lets Play with Art" features (mentor-uploaded artworks)
        return validFeatures.includes(feature);
      });

      // Group activities by artwork (originalImageUrl) and get unique artworks
      const artworkMap = new Map();
      filteredActivities.forEach(activity => {
        const artworkUrl = activity.originalImageUrl;
        
        if (!artworkUrl) return;
        
        // Get student name from various possible locations
        const studentName = activity.metadata?.artistName || 
                           activity.metadata?.artworkMeta?.artist || 
                           activity.metadata?.studentName || 
                           'Unknown Artist';
        
        // Only include artworks with student name, class, and section (mentor-uploaded)
        const className = activity.metadata?.className || '';
        const section = activity.metadata?.section || '';
        
        if (!studentName || studentName === 'Unknown Artist' || !className || !section) {
          return;
        }
        
        // Use artworkUrl as key to get unique artworks
        if (!artworkMap.has(artworkUrl)) {
          artworkMap.set(artworkUrl, {
            id: activity.id,
            artworkUrl: artworkUrl,
            title: activity.title || 'Untitled Artwork',
            artist: studentName,
            className: className,
            section: section,
            createdAt: activity.createdAt
          });
        }
      });

      // Convert map to array and sort by creation date (newest first)
      const artworksArray = Array.from(artworkMap.values()).sort((a, b) => {
        const ad = a.createdAt?.toDate ? a.createdAt.toDate() : a.createdAt;
        const bd = b.createdAt?.toDate ? b.createdAt.toDate() : b.createdAt;
        return new Date(bd) - new Date(ad);
      });

      console.log('Fetched mentor artworks for library:', artworksArray.length);
      setMentorArtworks(artworksArray);
      setFilteredMentorArtworks(artworksArray);
    } catch (error) {
      console.error('Error fetching mentor artworks:', error);
      setMentorArtworks([]);
      setFilteredMentorArtworks([]);
    } finally {
      setLoadingMentorArtworks(false);
    }
  };

  // Apply filters to mentor artworks
  useEffect(() => {
    if (!mentorArtworks.length) {
      setFilteredMentorArtworks([]);
      return;
    }

    let filtered = mentorArtworks;

    // Filter by artist name
    if (filterMentorArtist) {
      filtered = filtered.filter(artwork =>
        (artwork.artist || '').toLowerCase().includes(filterMentorArtist.toLowerCase()) ||
        (artwork.title || '').toLowerCase().includes(filterMentorArtist.toLowerCase())
      );
    }

    // Filter by class
    if (filterMentorClass) {
      filtered = filtered.filter(artwork => artwork.className === filterMentorClass);
    }

    // Filter by section
    if (filterMentorSection) {
      filtered = filtered.filter(artwork => artwork.section === filterMentorSection);
    }

    setFilteredMentorArtworks(filtered);
  }, [mentorArtworks, filterMentorArtist, filterMentorClass, filterMentorSection]);

  // Handle mentor artwork selection
  const handleSelectMentorArtwork = (artwork) => {
    console.log('🎨 MentorMagicPage: Selected mentor artwork:', artwork);
    
    // Normalize the artwork URL
    const normalizedArtworkUrl = getFullImageUrl(artwork.artworkUrl);
    
    // Store artwork data
    localStorage.setItem('mentorSelectedArtwork', JSON.stringify({
      file: normalizedArtworkUrl,
      name: artwork.title || 'Mentor Artwork',
      artist: artwork.artist || 'Unknown Artist',
      feature: selectedFeature || 'ways-of-seeing',
      isMentorUpload: true,
      className: artwork.className,
      section: artwork.section
    }));
    
    // Navigate based on selected feature
    const artworkState = {
      imageUrl: normalizedArtworkUrl,
      originalArtwork: normalizedArtworkUrl,
      url: normalizedArtworkUrl,
      artworkUrl: normalizedArtworkUrl,
      title: artwork.title || 'Mentor Artwork',
      artist: artwork.artist || 'Unknown Artist'
    };
    
    console.log('🎨 MentorMagicPage: Navigating with artwork state:', artworkState);
    console.log('🎨 MentorMagicPage: Selected feature:', selectedFeature);
    
    // Close the library modal
    setShowMentorArtworkLibrary(false);
    setShowArtworkSelection(false);
    
    // Navigate to the appropriate page based on feature
    const featureRoutes = {
      'appreciation': '/magic',
      'ways-of-seeing': '/art-world/selection',
      'reimagine': '/reimagine-studio'
    };
    
    const route = featureRoutes[selectedFeature] || '/art-world/selection';
    
    navigate(route, { 
      state: { 
        fromMentorDashboard: true,
        artwork: artworkState
      } 
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50/30 to-orange-50/20 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-72 h-72 bg-amber-300 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-orange-300 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-1000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-2000"></div>
      </div>

      {/* Header */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-12">
          <button
            onClick={() => navigate('/mentor-dashboard')}
            className="group flex items-center gap-3 px-6 py-3 bg-white/80 backdrop-blur-sm text-amber-700 rounded-2xl hover:bg-white hover:shadow-lg transition-all duration-300 border border-amber-200/50"
          >
            <ArrowRight className="w-5 h-5 rotate-180 group-hover:-translate-x-1 transition-transform" />
            <span className="font-semibold">Back to Dashboard</span>
          </button>
          
          <div className="text-center">
            <h1 className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-600 via-orange-500 to-amber-700 mb-2">
              Mentor Magic Studio
            </h1>
            <p className="text-black text-lg font-medium">Transform learning through art</p>
          </div>
          
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="group flex items-center gap-3 px-6 py-3 bg-white/80 backdrop-blur-sm text-amber-700 rounded-2xl hover:bg-white hover:shadow-lg transition-all duration-300 border border-amber-200/50"
            >
              <Home className="w-5 h-5 group-hover:scale-110 transition-transform" />
              <span className="font-semibold">Artist Corner</span>
            </button>
            
            <button
              onClick={() => navigate('/collections')}
              className="group flex items-center gap-3 px-6 py-3 bg-white/80 backdrop-blur-sm text-amber-700 rounded-2xl hover:bg-white hover:shadow-lg transition-all duration-300 border border-amber-200/50"
            >
              <Layers className="w-5 h-5 group-hover:scale-110 transition-transform" />
              <span className="font-semibold">Collections</span>
            </button>
          </div>
        </div>

        {/* Class Artworks Section */}
        {uploadedArtworks.length > 0 && (
          <div className="mb-12">
            <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-amber-200/50 relative overflow-hidden">
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-200/20 to-orange-200/20 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-yellow-200/20 to-amber-200/20 rounded-full translate-y-12 -translate-x-12"></div>
              
              <div className="relative z-10">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-green-400 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg">
                    <Check className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-800 mb-1">
                      Today's Class Artworks
                    </h2>
                    <p className="text-amber-600 font-medium">
                      {uploadedArtworks.length} artwork{uploadedArtworks.length !== 1 ? 's' : ''} ready for processing
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {uploadedArtworks.map((artwork) => (
                    <div key={artwork.id} className="group">
                      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 shadow-lg border border-amber-200/50 hover:shadow-xl hover:border-amber-300/50 transition-all duration-300 group-hover:scale-105">
                        <div className="aspect-square mb-4 rounded-xl overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 relative">
                          <img
                            src={artwork.preview}
                            alt={artwork.name}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </div>
                        
                        <div className="mb-4">
                          <h3 className="font-bold text-gray-800 truncate mb-1">{artwork.name}</h3>
                          <span className="inline-block px-3 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full capitalize">
                            {artwork.feature.replace('-', ' ')}
                          </span>
                        </div>
                        
                        <div className="flex gap-2">
                          <button
                            onClick={() => processArtwork(artwork)}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all duration-300 text-sm font-semibold shadow-lg hover:shadow-xl"
                          >
                            <ArrowRight className="w-4 h-4" />
                            Process
                          </button>
                          <button
                            onClick={() => removeArtwork(artwork.id)}
                            className="px-4 py-2.5 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all duration-300"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Artwork Selection Modal */}
        {showArtworkSelection && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleCloseArtworkSelection} />
            <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl max-w-6xl w-full max-h-[90vh] border border-amber-200/50 overflow-hidden">
              {/* Header */}
              <div className={`bg-gradient-to-r ${features.find(f => f.id === selectedFeature)?.color} text-white p-8 relative overflow-hidden`}>
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-12 -translate-x-12"></div>
                
                <div className="relative z-10 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {selectedFeature && (
                      <>
                        <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                          {React.createElement(features.find(f => f.id === selectedFeature)?.icon, { className: "w-6 h-6" })}
                        </div>
                        <div>
                          <h2 className="text-3xl font-bold">
                            {features.find(f => f.id === selectedFeature)?.title}
                          </h2>
                          <p className="text-white/90 text-lg">
                            Select an artwork to analyze
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                  <button 
                    onClick={handleCloseArtworkSelection} 
                    className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-2xl flex items-center justify-center transition-all duration-300 backdrop-blur-sm"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 overflow-y-auto max-h-[calc(90vh-200px)]">
                {/* Filters */}
                <div className="mb-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Search by Artist</label>
                    <input
                      type="text"
                      value={filterArtist}
                      onChange={(e) => setFilterArtist(e.target.value)}
                      placeholder="Type artist name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Class</label>
                    <select
                      value={filterClass}
                      onChange={(e) => setFilterClass(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300"
                    >
                      <option value="">All Classes</option>
                      {Array.from(new Set(previousArtworks.map(a => a.className).filter(Boolean))).map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Section</label>
                    <select
                      value={filterSection}
                      onChange={(e) => setFilterSection(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300"
                    >
                      <option value="">All Sections</option>
                      {Array.from(new Set(previousArtworks.map(a => a.section).filter(Boolean))).map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Upload New Artwork, Artwork Library, and Mentor Artwork Library Options - Centered */}
                  <div className="flex justify-center gap-4 flex-nowrap overflow-x-auto">
                    <div 
                      onClick={() => {
                        setShowArtworkSelection(false);
                        setShowUploadModal(true);
                      }}
                      className="group cursor-pointer bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-dashed border-amber-300 rounded-xl p-4 text-center hover:border-amber-400 hover:shadow-lg transition-all duration-300 flex-shrink-0 w-64"
                    >
                      <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                        <Upload className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-base font-bold text-gray-800 mb-1">Upload New Artwork</h3>
                      <p className="text-sm text-gray-600">Upload a new student artwork for analysis</p>
                    </div>
                    
                    {/* Artwork Library - Only for Ways of Seeing */}
                    {selectedFeature === 'ways-of-seeing' && (
                      <div 
                        onClick={() => {
                          setShowArtworkLibrary(true);
                          fetchArtHistoryArtworks();
                        }}
                        className="group cursor-pointer bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-dashed border-amber-300 rounded-xl p-4 text-center hover:border-amber-400 hover:shadow-lg transition-all duration-300 flex-shrink-0 w-64"
                      >
                        <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                          <ImageIcon className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="text-base font-bold text-gray-800 mb-1">Artwork Library</h3>
                        <p className="text-sm text-gray-600">Select from pre-loaded art history artworks</p>
                      </div>
                    )}
                    
                    {/* Mentor Artwork Library - For all features */}
                    <div 
                      onClick={() => {
                        setShowMentorArtworkLibrary(true);
                        fetchMentorArtworks();
                      }}
                      className="group cursor-pointer bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-dashed border-amber-300 rounded-xl p-4 text-center hover:border-amber-400 hover:shadow-lg transition-all duration-300 flex-shrink-0 w-64"
                    >
                      <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                        <History className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-base font-bold text-gray-800 mb-1">Mentor Artwork Library</h3>
                      <p className="text-sm text-gray-600">Select from your previously uploaded artworks</p>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        )}

        {/* Features Selection Modal Popup */}
        {showUploadModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleCloseModal} />
            <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl max-w-6xl w-full max-h-[90vh] border border-amber-200/50 overflow-hidden">
              {/* Header */}
              <div className={`bg-gradient-to-r ${features.find(f => f.id === selectedFeature)?.color} text-white p-8 relative overflow-hidden`}>
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-12 -translate-x-12"></div>
                
                <div className="relative z-10 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {selectedFeature && (
                      <>
                        <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                          {React.createElement(features.find(f => f.id === selectedFeature)?.icon, { className: "w-6 h-6" })}
                        </div>
                        <div>
                          <h2 className="text-3xl font-bold">
                            {features.find(f => f.id === selectedFeature)?.title}
                          </h2>
                          <p className="text-white/90 text-lg">
                            Upload student artworks for analysis
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                  <button 
                    onClick={handleCloseModal} 
                    className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-2xl flex items-center justify-center transition-all duration-300 backdrop-blur-sm"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 overflow-y-auto max-h-[calc(90vh-200px)]">
                {!uploadedArtwork ? (
                  /* Upload Area */
                  <div
                    className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-300 ${
                      dragActive 
                        ? 'border-amber-400 bg-gradient-to-br from-amber-50 to-orange-50 scale-105' 
                        : 'border-amber-200 hover:border-amber-300 bg-gradient-to-br from-gray-50 to-amber-50/30'
                    }`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                  >
                    <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-amber-400 to-orange-400 rounded-3xl flex items-center justify-center shadow-lg">
                      <Upload className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-3">
                      Upload Artworks
                    </h3>
                    <p className="text-gray-600 mb-6 text-lg">
                      Drag and drop your student artworks here, or click to browse files
                    </p>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-2xl hover:from-amber-600 hover:to-orange-600 transition-all duration-300 text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      Choose Files
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) => handleFileUpload(e.target.files)}
                      className="hidden"
                    />
                  </div>
                ) : (
                  /* Uploaded Artwork Display */
                  <div className="space-y-6">
                    <div className="text-center">
                      <h3 className="text-2xl font-bold text-gray-800 mb-2">
                        Artwork Ready for Processing
                      </h3>
                      <p className="text-gray-600 text-lg">
                        Please provide the artwork details and click "Process" to analyze it.
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Artwork Display - Maximized */}
                      <div className="bg-white rounded-2xl p-6 shadow-lg border border-amber-200">
                        <div className="w-full h-96 rounded-xl overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
                          <img
                            src={uploadedArtwork.preview}
                            alt={uploadedArtwork.name}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      </div>
                      
                      {/* Artwork Details Form */}
                      <div className="bg-white rounded-2xl p-6 shadow-lg border border-amber-200">
              <h4 className="text-xl font-bold text-gray-800 mb-6">Artwork Information</h4>
                        
                        <div className="space-y-6">
                {/* Class & Section selectors */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Class</label>
                    <select
                      value={classNameSelection}
                      onChange={(e) => { setClassNameSelection(e.target.value); try { localStorage.setItem('mentorClass', e.target.value); } catch {} }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300 text-lg"
                    >
                      <option value="">Select Class</option>
                      <option value="Class 1">Class 1</option>
                      <option value="Class 2">Class 2</option>
                      <option value="Class 3">Class 3</option>
                      <option value="Class 4">Class 4</option>
                      <option value="Class 5">Class 5</option>
                      <option value="Class 6">Class 6</option>
                      <option value="Class 7">Class 7</option>
                      <option value="Class 8">Class 8</option>
                      <option value="Class 9">Class 9</option>
                      <option value="Class 10">Class 10</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Section</label>
                    <select
                      value={sectionSelection}
                      onChange={(e) => { setSectionSelection(e.target.value); try { localStorage.setItem('mentorSection', e.target.value); } catch {} }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300 text-lg"
                    >
                      <option value="">Select Section</option>
                      <option value="A">A</option>
                      <option value="B">B</option>
                      <option value="C">C</option>
                      <option value="D">D</option>
                    </select>
                  </div>
                </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Artwork Name *
                            </label>
                            <input
                              type="text"
                              value={artworkName}
                              onChange={(e) => setArtworkName(e.target.value)}
                              placeholder="Enter the artwork title"
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300 text-lg"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Artist Name *
                            </label>
                            <input
                              type="text"
                              value={artistName}
                              onChange={(e) => setArtistName(e.target.value)}
                              placeholder="Enter the artist's name"
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300 text-lg"
                            />
                          </div>
                          
                          <div className="pt-4">
                            <div className="flex gap-4">
                              <button
                                onClick={handleProcessArtwork}
                                disabled={!artworkName.trim() || !artistName.trim()}
                                className="flex-1 px-6 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-2xl hover:from-amber-600 hover:to-orange-600 transition-all duration-300 text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                              >
                                <ArrowRight className="w-5 h-5" />
                                Process Artwork
                              </button>
                              <button
                                onClick={() => setUploadedArtwork(null)}
                                className="px-6 py-4 bg-gray-500 text-white rounded-2xl hover:bg-gray-600 transition-all duration-300 text-lg font-semibold shadow-lg hover:shadow-xl"
                              >
                                Upload Another
                              </button>
                            </div>
                            
                            {(!artworkName.trim() || !artistName.trim()) && (
                              <p className="text-red-500 text-sm mt-2 text-center">
                                Please fill in both artwork name and artist name to continue
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Main Features Grid */}
        <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-12 shadow-2xl border border-amber-200/50 relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-40 h-40 bg-gradient-to-br from-amber-200/10 to-orange-200/10 rounded-full -translate-y-20 -translate-x-20"></div>
          <div className="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tl from-yellow-200/10 to-amber-200/10 rounded-full translate-y-16 translate-x-16"></div>
          
          <div className="relative z-10">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">Choose Your Teaching Tool</h2>
              <p className="text-amber-600 text-xl font-medium max-w-2xl mx-auto">
                Select a feature to upload and process student artworks for your class
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div
                  key={feature.id}
                  onClick={() => handleFeatureSelect(feature.id)}
                  className="group cursor-pointer"
                >
                  <div className={`${feature.bgColor} ${feature.borderColor} border-2 rounded-3xl p-8 hover:shadow-2xl transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 relative overflow-hidden`}>
                    {/* Decorative background */}
                    <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${feature.color} opacity-10 rounded-full -translate-y-12 translate-x-12`}></div>
                    <div className={`absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr ${feature.color} opacity-5 rounded-full translate-y-8 -translate-x-8`}></div>
                    
                    <div className="relative z-10 text-center">
                      <div className={`w-20 h-20 mx-auto mb-6 rounded-3xl bg-gradient-to-r ${feature.color} flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform duration-300`}>
                        <feature.icon className="w-10 h-10 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-800 mb-4 group-hover:text-gray-900 transition-colors">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                        {feature.description}
                      </p>
                      <button className={`w-full px-8 py-4 bg-gradient-to-r ${feature.color} text-white rounded-2xl hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3 text-lg font-semibold group-hover:scale-105`}>
                        Upload Artworks
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Artwork Library Modal */}
      {showArtworkLibrary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowArtworkLibrary(false)} />
          <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl max-w-6xl w-full max-h-[90vh] border border-amber-200/50 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-12 -translate-x-12"></div>
              
              <div className="relative z-10 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                    <ImageIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold">Artwork Library</h2>
                    <p className="text-white/90 text-lg">Select from pre-loaded art history artworks</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowArtworkLibrary(false)} 
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-2xl flex items-center justify-center transition-all duration-300 backdrop-blur-sm"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 overflow-y-auto max-h-[calc(90vh-200px)]">
              {loadingArtHistory ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
                  <span className="ml-3 text-amber-700 text-lg">Loading artworks...</span>
                </div>
              ) : artHistoryArtworks.length === 0 ? (
                <div className="text-center py-12">
                  <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">No Artworks Available</h3>
                  <p className="text-gray-500">No art history artworks found in the database.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Artwork Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {artHistoryArtworks.map((artwork, index) => (
                      <div
                        key={artwork.id}
                        onClick={() => handleSelectArtHistoryArtwork(artwork)}
                        className="group cursor-pointer bg-white border-2 border-gray-200 rounded-2xl p-4 hover:border-amber-400 hover:shadow-lg transition-all duration-300"
                      >
                        <div className="aspect-square mb-4 rounded-xl overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 relative">
                          <img
                            src={artwork.imageUrl}
                            alt={artwork.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                            onError={(e) => {
                              console.error('❌ Failed to load artwork image:', artwork.imageUrl);
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </div>
                        
                        <div className="mb-3">
                          <h3 className="font-bold text-gray-800 truncate mb-1">{artwork.title || 'Untitled'}</h3>
                          <p className="text-gray-600 text-sm">by {artwork.artist || 'Unknown Artist'}</p>
                        </div>
                        
                        {artwork.artMovement && (
                          <div className="mb-3">
                            <span className="inline-block px-2 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                              {artwork.artMovement}
                            </span>
                          </div>
                        )}
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectArtHistoryArtwork(artwork);
                          }}
                          className="w-full px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all duration-300 text-sm font-semibold"
                        >
                          Select & Apply Style Transfer
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  {/* Artwork Count */}
                  <div className="text-center pt-4">
                    <p className="text-gray-600 text-sm">
                      {artHistoryArtworks.length} artwork{artHistoryArtworks.length !== 1 ? 's' : ''} available
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mentor Artwork Library Modal */}
      {showMentorArtworkLibrary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => {
            setShowMentorArtworkLibrary(false);
            setFilterMentorArtist('');
            setFilterMentorClass('');
            setFilterMentorSection('');
          }} />
          <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl max-w-6xl w-full max-h-[90vh] border border-amber-200/50 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-12 -translate-x-12"></div>
              
              <div className="relative z-10 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                    <History className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold">Mentor Artwork Library</h2>
                    <p className="text-white/90 text-lg">Select from your previously uploaded artworks</p>
                  </div>
                </div>
                <button 
                  onClick={() => {
                    setShowMentorArtworkLibrary(false);
                    setFilterMentorArtist('');
                    setFilterMentorClass('');
                    setFilterMentorSection('');
                  }} 
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-2xl flex items-center justify-center transition-all duration-300 backdrop-blur-sm"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 overflow-y-auto max-h-[calc(90vh-200px)]">
              {loadingMentorArtworks ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
                  <span className="ml-3 text-amber-700 text-lg">Loading artworks...</span>
                </div>
              ) : mentorArtworks.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">No Artworks Available</h3>
                  <p className="text-gray-500">No previously uploaded artworks found. Upload your first artwork to get started.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Filters */}
                  <div className="mb-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Search by Name/Artist</label>
                      <input
                        type="text"
                        value={filterMentorArtist}
                        onChange={(e) => setFilterMentorArtist(e.target.value)}
                        placeholder="Type artist or artwork name"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Class</label>
                      <select
                        value={filterMentorClass}
                        onChange={(e) => setFilterMentorClass(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300"
                      >
                        <option value="">All Classes</option>
                        {Array.from(new Set(mentorArtworks.map(a => a.className).filter(Boolean))).sort().map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Section</label>
                      <select
                        value={filterMentorSection}
                        onChange={(e) => setFilterMentorSection(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300"
                      >
                        <option value="">All Sections</option>
                        {Array.from(new Set(mentorArtworks.map(a => a.section).filter(Boolean))).sort().map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Artwork Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredMentorArtworks.map((artwork, index) => (
                      <div
                        key={artwork.id || index}
                        onClick={() => handleSelectMentorArtwork(artwork)}
                        className="group cursor-pointer bg-white border-2 border-gray-200 rounded-2xl p-4 hover:border-amber-400 hover:shadow-lg transition-all duration-300"
                      >
                        <div className="aspect-square mb-4 rounded-xl overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 relative">
                          <img
                            src={getFullImageUrl(artwork.artworkUrl)}
                            alt={artwork.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                            onError={(e) => {
                              console.error('❌ Failed to load artwork image:', artwork.artworkUrl);
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </div>
                        
                        <div className="mb-3">
                          <h3 className="font-bold text-gray-800 truncate mb-1">{artwork.title || 'Untitled'}</h3>
                          <p className="text-gray-600 text-sm">by {artwork.artist || 'Unknown Artist'}</p>
                        </div>
                        
                        {(artwork.className || artwork.section) && (
                          <div className="mb-3">
                            <span className="inline-block px-2 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                              {artwork.className} {artwork.section && `- ${artwork.section}`}
                            </span>
                          </div>
                        )}
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectMentorArtwork(artwork);
                          }}
                          className="w-full px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all duration-300 text-sm font-semibold"
                        >
                          {selectedFeature === 'appreciation' ? 'Select for Art Appreciation' :
                           selectedFeature === 'reimagine' ? 'Select for Re-imagine' :
                           'Select & Apply Style Transfer'}
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  {/* Artwork Count */}
                  <div className="text-center pt-4">
                    <p className="text-gray-600 text-sm">
                      {filteredMentorArtworks.length} of {mentorArtworks.length} artwork{mentorArtworks.length !== 1 ? 's' : ''} {filteredMentorArtworks.length !== mentorArtworks.length ? 'matching filters' : 'available'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
